import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, CloudUpload, Delete } from '@mui/icons-material';
import { trustMastersAPI, API_BASE_URL } from '../services/api';
import { showError } from '../utils/toast';
import { colors } from '../theme/modernTheme';

/**
 * Trust Master Form Component
 * Handles creation and editing of trust master records
 *
 * @param {Object} props
 * @param {Object} [props.trustMaster] - Trust master object for edit mode
 * @param {Function} props.onSave - Callback function when form is saved
 * @param {Function} props.onCancel - Callback function when form is cancelled
 */
export default function TrustMasterForm({ trustMaster, onSave, onCancel }) {
  const isEditMode = Boolean(trustMaster);

  // Form state
  const [formData, setFormData] = useState({
    trustName: '',
    receiptPrefix: '',
    notes: '',
    footerText: '',
    whatsappTemplateId: '',
    emailTemplateId: '',
  });

  // File state
  const [files, setFiles] = useState({
    headerImage: null,
    footerImage: null,
  });

  // Preview URLs for uploaded files
  const [previews, setPreviews] = useState({
    headerImage: null,
    footerImage: null,
  });

  // Track which images should be deleted
  const [imagesToDelete, setImagesToDelete] = useState({
    headerImage: false,
    footerImage: false,
  });

  // Template options
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Load trust master data in edit mode
  // Helper function to get proper image URL from path
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // Handle both absolute paths and relative paths
    // Absolute paths contain the full file system path, we need to extract the uploads/... part
    // Look for 'uploads/' in the path and take everything from there
    const uploadsIndex = imagePath.indexOf('uploads/');
    let relativePath;
    if (uploadsIndex !== -1) {
      relativePath = '/' + imagePath.substring(uploadsIndex);
    } else {
      // If it's already a relative path starting with uploads
      relativePath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    }
    // Prepend the backend base URL (remove /api/v1 suffix to get base URL)
    const backendBaseUrl = API_BASE_URL.replace('/api/v1', '');
    return `${backendBaseUrl}${relativePath}`;
  };

  useEffect(() => {
    if (isEditMode && trustMaster) {
      setFormData({
        trustName: trustMaster.trustName || '',
        receiptPrefix: trustMaster.receiptPrefix || '',
        notes: trustMaster.notes || '',
        footerText: trustMaster.footerText || '',
        whatsappTemplateId: trustMaster.whatsappTemplateId || '',
        emailTemplateId: trustMaster.emailTemplateId || '',
      });

      // Set preview URLs for existing images (convert paths to proper URLs)
      setPreviews({
        headerImage: getImageUrl(trustMaster.headerImagePath),
        footerImage: getImageUrl(trustMaster.footerImagePath),
      });
    }
  }, [isEditMode, trustMaster]);

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const [whatsappResponse, emailResponse] = await Promise.all([
        trustMastersAPI.getWhatsAppTemplates(),
        trustMastersAPI.getEmailTemplates(),
      ]);

      // Handle different response structures
      const whatsappData = whatsappResponse?.data?.templates || [];
      const emailData = emailResponse.data?.data || emailResponse.data || [];

      setWhatsappTemplates(Array.isArray(whatsappData) ? whatsappData : []);
      setEmailTemplates(Array.isArray(emailData) ? emailData : []);
    } catch (err) {
      console.error('Error loading templates:', err);
      // Set empty arrays on error to prevent map errors
      setWhatsappTemplates([]);
      setEmailTemplates([]);
      showError('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Real-time validation on change
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error || null }));
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'trustName':
        if (!value || value.trim() === '') return 'Trust name is required';
        if (value.length > 100) return 'Trust name cannot exceed 100 characters';
        return '';
      case 'receiptPrefix':
        if (!value || value.trim() === '') return 'Receipt prefix is required';
        if (!/^[A-Za-z0-9]*$/.test(value))
          return 'Receipt prefix must contain only alphanumeric characters';
        if (value.length > 5) return 'Receipt prefix cannot exceed 5 characters';
        return '';
      case 'notes':
        if (value && value.length > 2000) return 'Notes cannot exceed 2000 characters';
        return '';
      case 'footerText':
        if (value && value.length > 300) return 'Footer text cannot exceed 300 characters';
        return '';
      default:
        return '';
    }
  };

  // Image dimension requirements
  const IMAGE_REQUIREMENTS = {
    headerImage: {
      width: 1584,
      height: 396,
      label: 'Header image',
    },
    footerImage: {
      width: 1584,
      height: 396,
      label: 'Footer image',
    },
  };

  const validateImageDimensions = (file, imageType) => {
    return new Promise((resolve) => {
      const req = IMAGE_REQUIREMENTS[imageType];
      if (!req) {
        resolve(null);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        if (width !== req.width || height !== req.height) {
          resolve(
            `${req.label} must be exactly ${req.width}px × ${req.height}px (current: ${width}px × ${height}px)`,
          );
        } else {
          resolve(null);
        }
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        resolve('Could not read image dimensions');
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [imageType]: 'Only JPG, JPEG, and PNG files are allowed',
      }));
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [imageType]: 'File size must not exceed 5MB',
      }));
      return;
    }

    // Validate image dimensions
    const dimensionError = await validateImageDimensions(file, imageType);
    if (dimensionError) {
      setErrors((prev) => ({
        ...prev,
        [imageType]: dimensionError,
      }));
      return;
    }

    // Clear error
    setErrors((prev) => ({ ...prev, [imageType]: null }));

    // Set file
    setFiles((prev) => ({
      ...prev,
      [imageType]: file,
    }));

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews((prev) => ({
        ...prev,
        [imageType]: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = (imageType) => {
    setFiles((prev) => ({
      ...prev,
      [imageType]: null,
    }));
    setPreviews((prev) => ({
      ...prev,
      [imageType]: null,
    }));
    // Mark image for deletion if in edit mode and there was an existing image
    if (isEditMode && trustMaster) {
      const hasExistingImage =
        (imageType === 'headerImage' && trustMaster.headerImagePath) ||
        (imageType === 'footerImage' && trustMaster.footerImagePath);

      if (hasExistingImage) {
        setImagesToDelete((prev) => ({
          ...prev,
          [imageType]: true,
        }));
      }
    }
    // Clear error
    setErrors((prev) => ({ ...prev, [imageType]: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Trust name validation
    if (!formData.trustName || formData.trustName.trim() === '') {
      newErrors.trustName = 'Trust name is required';
    } else if (formData.trustName.length > 100) {
      newErrors.trustName = 'Trust name cannot exceed 100 characters';
    }

    // Receipt prefix validation (mandatory)
    if (!formData.receiptPrefix || formData.receiptPrefix.trim() === '') {
      newErrors.receiptPrefix = 'Receipt prefix is required';
    } else if (!/^[A-Za-z0-9]*$/.test(formData.receiptPrefix)) {
      newErrors.receiptPrefix = 'Receipt prefix must contain only alphanumeric characters';
    } else if (formData.receiptPrefix.length > 5) {
      newErrors.receiptPrefix = 'Receipt prefix cannot exceed 5 characters';
    }

    // Notes validation (optional but must be valid if provided)
    if (formData.notes && formData.notes.length > 2000) {
      newErrors.notes = 'Notes cannot exceed 2000 characters';
    }

    // Footer text validation (optional but must be valid if provided)
    if (formData.footerText && formData.footerText.length > 300) {
      newErrors.footerText = 'Footer text cannot exceed 300 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Create FormData for multipart/form-data submission
      const formDataToSend = new FormData();
      formDataToSend.append('trustName', formData.trustName.trim());

      if (formData.receiptPrefix) {
        formDataToSend.append('receiptPrefix', formData.receiptPrefix.trim());
      }

      if (formData.notes) {
        formDataToSend.append('notes', formData.notes.trim());
      }

      if (formData.footerText) {
        formDataToSend.append('footerText', formData.footerText.trim());
      }

      // Always send template IDs (including empty string for "None")
      formDataToSend.append('whatsappTemplateId', formData.whatsappTemplateId || '');
      formDataToSend.append('emailTemplateId', formData.emailTemplateId || '');

      // Append files if they exist
      if (files.headerImage) {
        formDataToSend.append('headerImage', files.headerImage);
      } else if (isEditMode && imagesToDelete.headerImage) {
        // Send flag to delete header image
        formDataToSend.append('deleteHeaderImage', 'true');
      }

      if (files.footerImage) {
        formDataToSend.append('footerImage', files.footerImage);
      } else if (isEditMode && imagesToDelete.footerImage) {
        // Send flag to delete footer image
        formDataToSend.append('deleteFooterImage', 'true');
      }

      let response;
      if (isEditMode) {
        response = await trustMastersAPI.update(trustMaster.id, formDataToSend);
      } else {
        response = await trustMastersAPI.create(formDataToSend);
      }

      // Call onSave callback with response data
      if (onSave) {
        onSave(response.data);
      }
    } catch (err) {
      console.error('Error saving trust master:', err);

      // Handle validation errors from backend
      if (err.error?.code === 'VALIDATION_ERROR') {
        const backendErrors = {};
        // Parse details array (field_errors or details)
        const details = err.error.details?.field_errors || err.error.details || [];
        if (Array.isArray(details)) {
          details.forEach((d) => {
            if (d.field) backendErrors[d.field] = d.message;
          });
        } else if (typeof details === 'object') {
          Object.assign(backendErrors, details);
        }
        setErrors(backendErrors);
      } else {
        showError(err?.error?.message || 'Failed to save trust master');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFileUpload = (imageType, label) => {
    const hasPreview = previews[imageType];
    const error = errors[imageType];

    return (
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            textTransform: 'capitalize',
            fontWeight: 500,
            color: colors.text.primary,
          }}
        >
          {label}
        </Typography>

        {hasPreview ? (
          <Card
            sx={{
              position: 'relative',
              border: `1px solid ${colors.border.main}`,
              borderRadius: '4px',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 150,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: colors.background.default,
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={previews[imageType]}
                  alt={label}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(imageType)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUpload />}
            fullWidth
            sx={{
              py: 2,
              borderRadius: '4px',
              borderStyle: 'dashed',
              borderColor: error ? colors.error.main : colors.text.main,
              color: error ? colors.error.main : colors.text.secondary,
              '&:hover': {
                borderStyle: 'dashed',
                borderColor: error ? colors.error.dark : colors.primary.main,
                backgroundColor: colors.action.hover,
              },
            }}
          >
            Upload {label}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png"
              onChange={(e) => handleFileChange(e, imageType)}
            />
          </Button>
        )}

        {error && (
          <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
            {error}
          </FormHelperText>
        )}

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: colors.text.secondary,
          }}
        >
          JPG, JPEG, or PNG (max 5MB). Required: 1584px × 396px
        </Typography>
      </Box>
    );
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Row 1: Trust Name and Receipt Prefix */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Trust Name"
              name="trustName"
              value={formData.trustName}
              onChange={handleChange}
              required
              autoFocus
              error={!!errors.trustName}
              helperText={errors.trustName || `${formData.trustName.length}/50`}
              inputProps={{
                maxLength: 50,
              }}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginRight: 0,
                  textAlign: errors.trustName ? 'left' : 'right',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="Receipt Prefix"
              name="receiptPrefix"
              value={formData.receiptPrefix}
              onChange={handleChange}
              error={!!errors.receiptPrefix}
              helperText={
                errors.receiptPrefix || `Alphanumeric only. ${formData.receiptPrefix.length}/5`
              }
              inputProps={{
                maxLength: 5,
              }}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginRight: 0,
                  textAlign: errors.receiptPrefix ? 'left' : 'right',
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Row 2: Images */}
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderFileUpload('headerImage', 'Header Image')}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderFileUpload('footerImage', 'Footer Image')}
            </Grid>
          </Grid>
        </Box>

        {/* Row 3: Footer Text */}
        <Box>
          <TextField
            fullWidth
            label="Footer Text (Optional)"
            name="footerText"
            value={formData.footerText}
            onChange={handleChange}
            multiline
            rows={4}
            placeholder="Enter footer text for receipts..."
            error={!!errors.footerText}
            helperText={errors.footerText || `${formData.footerText.length}/300`}
            inputProps={{
              maxLength: 300,
            }}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
                marginRight: 0,
                textAlign: errors.footerText ? 'left' : 'right',
              },
            }}
          />
        </Box>

        {/* Row 4: Communication Templates side by side */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 1,
              textTransform: 'capitalize',
              fontWeight: 500,
              color: colors.text.primary,
            }}
          >
            Communication Templates
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Email Template (Optional)</InputLabel>
                <Select
                  name="emailTemplateId"
                  value={formData.emailTemplateId}
                  onChange={handleChange}
                  label="Email Template (Optional)"
                  disabled={loadingTemplates}
                >
                  <MenuItem value="">None</MenuItem>
                  {emailTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
                {loadingTemplates && <FormHelperText>Loading templates...</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>WhatsApp Template (Optional)</InputLabel>
                <Select
                  name="whatsappTemplateId"
                  value={formData.whatsappTemplateId}
                  onChange={handleChange}
                  label="WhatsApp Template (Optional)"
                  disabled={loadingTemplates}
                >
                  <MenuItem value="">None</MenuItem>
                  {whatsappTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
                {loadingTemplates && <FormHelperText>Loading templates...</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Row 5: Notes */}
        <TextField
          fullWidth
          label="Notes (Optional)"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          multiline
          rows={4}
          error={!!errors.notes}
          helperText={errors.notes || `${formData.notes.length}/2000`}
          inputProps={{
            maxLength: 2000,
          }}
          FormHelperTextProps={{
            sx: {
              marginLeft: 0,
              marginRight: 0,
              textAlign: errors.notes ? 'left' : 'right',
            },
          }}
        />

        {/* Form Actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            pt: 2,
            borderTop: `1px solid ${colors.border.main}`,
          }}
        >
          <Button
            onClick={onCancel}
            disabled={loading}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderColor: colors.error.main,
              color: colors.error.main,
              borderRadius: '4px',
              '&:hover': {
                borderColor: colors.error.dark,
                backgroundColor: 'rgba(220, 38, 38, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              backgroundColor: colors.primary.main,
              '&:hover': {
                backgroundColor: colors.primary.dark,
              },
            }}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
